/**
 * Trenbe Search 위젯 — 자체 검색 API 렌더러 (현행 UI 클론).
 * 계약: 백엔드 리포 docs/12. 선택 상태의 진실은 URL 쿼리스트링(replaceState 동기화).
 *
 * 레이아웃: PC = 좌측 상시 사이드바 / 모바일(<990px) = Refine By 드로어 — 같은 렌더러가
 * 두 컨테이너에 동일 내용을 그린다. 가격 KRW 는 통화앱(bucksCC.reConvert)이 .money 를 변환.
 */
(() => {
  const root = document.getElementById('trenbe-search');
  if (!root) return;

  const $ = (sel) => root.querySelector(sel);
  const els = {
    title: $('[data-ts-title]'),
    form: $('[data-ts-form]'),
    input: $('[data-ts-input]'),
    clearQ: $('[data-ts-clear-q]'),
    tabs: $('[data-ts-tabs]'),
    showing: $('[data-ts-showing]'),
    toolbar: $('[data-ts-toolbar]'),
    count: $('[data-ts-count]'),
    sort: $('[data-ts-sort]'),
    sortRoot: $('[data-ts-sort-root]'),
    sortBtn: $('[data-ts-sort-btn]'),
    sortValue: $('[data-ts-sort-value]'),
    sortList: $('[data-ts-sort-list]'),
    refine: $('[data-ts-refine]'),
    prev: $('[data-ts-prev]'),
    prevBtn: $('[data-ts-prev-btn]'),
    sidebar: $('[data-ts-sidebar]'),
    drawerFacets: $('[data-ts-drawer-facets]'),
    status: $('[data-ts-status]'),
    grid: $('[data-ts-grid]'),
    more: $('[data-ts-more]'),
    moreBtn: $('[data-ts-more-btn]'),
    overlay: $('[data-ts-overlay]'),
    drawer: $('[data-ts-drawer]'),
    drawerTitle: $('[data-ts-drawer-title]'),
    drawerBack: $('[data-ts-back]'),
    clearAll: $('[data-ts-clear]'),
    showResult: $('[data-ts-show-result]'),
    close: $('[data-ts-close]'),
    panelProducts: $('[data-ts-panel-products]'),
    panelResources: $('[data-ts-panel-resources]'),
  };

  // theme dev 로컬(http 페이지)에서 https dev API 를 부르면 자체 서명 인증서에
  // 막힌다 — 페이지가 http 면 내부(dev) 호스트에 한해 http 로 강등한다.
  let API_BASE = root.dataset.apiBase.replace(/\/$/, '');
  if (location.protocol === 'http:' && API_BASE.includes('.trenbekr.in')) {
    API_BASE = API_BASE.replace('https://', 'http://');
  }
  // 컬렉션 페이지 모드 — Liquid 가 핸들을 넣어준다 (검색 페이지에선 빈 값)
  const COLLECTION_HANDLE = root.dataset.collectionHandle || '';
  const COLLECTION_TITLE = root.dataset.collectionTitle || '';
  const LOCALE = ['en', 'ko', 'ja', 'de'].includes(root.dataset.locale)
    ? root.dataset.locale
    : 'en';
  const LIMIT = Number(root.dataset.pageLimit) || 40;
  const INSTORE_SELLERS = new Set(
    root.dataset.instoreSellers.split(',').map((s) => s.trim()),
  );
  const FACET_PARAMS = [
    'pf_c_category',
    'pf_v_brand',
    'pf_pt_product_type',
    'pf_m_condition',
    'pf_p_price',
  ];
  // 사이드바 파셋은 아니지만 API 로 그대로 전달하는 필터 파라미터.
  // pf_t_tags: monthly-deals 할인율 탭(템플릿의 커스텀 Liquid 섹션)이 쓰는
  // Boost 호환 태그 필터 — 탭 UI 가 URL 을 직접 관리하므로 Clear All 제외.
  const PASSTHROUGH_PARAMS = ['pf_t_tags'];
  const SINGLE_SELECT = new Set(['pf_c_category', 'pf_p_price']);
  // 현행(Boost) 데스크톱 사이드바 그룹 순서
  const GROUP_ORDER = [
    'pf_c_category',
    'pf_pt_product_type',
    'pf_v_brand',
    'pf_p_price',
    'pf_m_condition',
  ];
  const CATEGORY_TAG = /^(women|men|golf|kids|life|unisex)(_[a-z0-9]+)*$/i;
  const PRICE_SLIDER_MAX = 20000; // 통계 상한 미제공 — 입력창은 자유값 (백로그: stats agg)

  const state = {
    page: 1,
    minPage: 1, // 프리펜드 하한 — 1 보다 크면 Load Previous Page 노출
    totalPage: 1,
    loading: false,
    view: 'grid',
    brandFilter: '', // 브랜드 목록 검색어 (Search Options)
    openCats: new Set(), // 펼쳐진 카테고리 노드
    drawerGroup: null, // 모바일 드로어 드릴다운 — 열려 있는 그룹 id (null=루트 목록)
  };

  // ── URL 이 선택 상태의 진실 ─────────────────────────────

  function currentParams() {
    const src = new URLSearchParams(location.search);
    const dst = new URLSearchParams();
    for (const [key, value] of src) {
      const k = key.replace(/\[\]$/, '');
      if (
        k === 'q' ||
        k === 'sort' ||
        k === 'match' ||
        FACET_PARAMS.includes(k) ||
        PASSTHROUGH_PARAMS.includes(k)
      ) {
        dst.append(k, value);
      }
    }
    return dst;
  }

  function writeUrl(params) {
    const keep = new URLSearchParams(location.search);
    const managed = ['q', 'sort', 'match', 'page', ...FACET_PARAMS, ...PASSTHROUGH_PARAMS];
    for (const k of managed) keep.delete(k);
    for (const k of [...keep.keys()]) if (k.endsWith('[]')) keep.delete(k);
    const merged = new URLSearchParams([...keep, ...params]);
    history.replaceState(null, '', `${location.pathname}?${merged}`);
  }

  /** 현행처럼 스크롤이 내려간 페이지를 URL 에 유지 (?page=N) — 새로고침 복원용 */
  function syncPageParam(n) {
    const sp = new URLSearchParams(location.search);
    if (n > 1) sp.set('page', String(n));
    else sp.delete('page');
    history.replaceState(null, '', `${location.pathname}?${sp}`);
  }

  function toggleFacetValue(paramId, key) {
    const params = currentParams();
    const values = params.getAll(paramId);
    params.delete(paramId);
    // 대소문자 무관 비교 — API 키는 normalized 소문자(prada)지만 Boost 호환
    // URL 은 원표기(PRADA)로 들어올 수 있다. 불일치면 해제가 추가로 둔갑한다.
    const same = (v) => v.toLowerCase() === key.toLowerCase();
    if (SINGLE_SELECT.has(paramId)) {
      if (!values.some(same)) params.set(paramId, key); // 재클릭 = 해제
    } else {
      const next = values.some(same)
        ? values.filter((v) => !same(v))
        : [...values, key];
      for (const v of next) params.append(paramId, v);
    }
    writeUrl(params);
    load(1);
  }

  function setPriceRange(min, max) {
    const params = currentParams();
    params.delete('pf_p_price');
    if (min !== '' || max !== '') params.set('pf_p_price', `${min}:${max}`);
    writeUrl(params);
    load(1);
  }

  // ── API ────────────────────────────────────────────────

  async function fetchPage(page) {
    const params = currentParams();
    params.set('locale', LOCALE);
    params.set('limit', String(LIMIT));
    params.set('page', String(page));
    const endpoint = COLLECTION_HANDLE
      ? `/v1/collections/${encodeURIComponent(COLLECTION_HANDLE)}`
      : '/v1/search';
    const res = await fetch(`${API_BASE}${endpoint}?${params}`, {
      signal: AbortSignal.timeout(10000),
    });
    // 컬렉션 404 = 상품이 하나도 색인되지 않은 컬렉션 (API 는 소속 상품으로 컬렉션을 해석한다 —
    // 예: men-prada 는 Shopify 에 있지만 상품 0개). 페이지 자체는 Shopify 가 이미 200 으로 그렸으므로
    // 오류가 아니라 빈 목록으로 보여 준다
    if (res.status === 404 && COLLECTION_HANDLE) return emptyResult();
    if (!res.ok) throw new Error(`search api HTTP ${res.status}`);
    return res.json();
  }

  const emptyResult = () => ({
    products: [],
    total_product: 0,
    page: 1,
    total_page: 0,
    filter: { options: [] },
    meta: {},
  });

  /**
   * Collections / Blogs & Pages 탭 데이터.
   * 1차: 우리 search-api /v1/resources (제목+설명·본문 전문검색 — 현행 Boost 동등).
   * 폴백: Shopify 네이티브 suggest.json (제목 prefix 만 — API 미배포 시에도 동작).
   */
  async function fetchResources(q) {
    if (!q) return { collections: [], pages: [], counts: { c: 0, p: 0 } };
    try {
      const r = await fetch(
        `${API_BASE}/v1/resources?q=${encodeURIComponent(q)}&limit=10`,
        { signal: AbortSignal.timeout(5000) },
      );
      if (r.ok) {
        const d = await r.json();
        const pages = [
          ...(d.pages?.items || []),
          ...(d.articles?.items || []),
        ];
        return {
          collections: d.collections?.items || [],
          pages,
          counts: {
            c: d.collections?.total ?? 0,
            p: (d.pages?.total ?? 0) + (d.articles?.total ?? 0),
          },
        };
      }
    } catch {
      /* 폴백으로 진행 */
    }
    try {
      const r = await (
        await fetch(
          `/search/suggest.json?q=${encodeURIComponent(q)}` +
            '&resources[type]=collection,page,article&resources[limit]=10',
        )
      ).json();
      const res = r.resources.results;
      const collections = res.collections || [];
      const pages = [...(res.pages || []), ...(res.articles || [])];
      return {
        collections,
        pages,
        counts: { c: collections.length, p: pages.length },
      };
    } catch {
      return { collections: [], pages: [], counts: { c: 0, p: 0 } };
    }
  }

  // ── 렌더: 상품 카드 ────────────────────────────────────

  const money = (usd) =>
    `<span class="money">$${usd.toLocaleString('en-US')}</span>`;

  /**
   * 현행 실측: 같은 상품이 그리드에선 브랜드 접두어 제거("779901 Gg Supreme…"),
   * 리스트에선 원문("Gucci GG Marmont…")으로 나온다 — 뷰별로 다르게 렌더한다.
   * capitalize 는 CSS 가 담당.
   */
  function gridTitle(p) {
    let t = p.title;
    if (p.brand && t.toLowerCase().startsWith(p.brand.toLowerCase())) {
      t = t.slice(p.brand.length).trim();
    }
    return t.toLowerCase();
  }

  function listTitle(p) {
    return p.title.toLowerCase();
  }

  // 현행 카테고리 행 화살표 — Boost svg path 그대로 (좌향 채움 화살표를
  // CSS 로 회전: 접힘 180°=">", 펼침 -90°="⌄" — 현행과 동일한 각도 체계)
  const CAT_ARROW_SVG =
    '<svg width="7" height="12" viewBox="0 0 7 12" fill="#222" aria-hidden="true"><path d="M6.58942 0.610713C6.26398 0.285274 5.73635 0.285274 5.41091 0.610713L0.611415 5.41021C0.455135 5.56649 0.366829 5.77895 0.366829 5.99996C0.366829 6.22098 0.454629 6.43294 0.610909 6.58922L5.41091 11.3892C5.73635 11.7147 6.26398 11.7147 6.58942 11.3892C6.91486 11.0638 6.91486 10.5362 6.58942 10.2107L2.37867 5.99996L6.58942 1.78922C6.91486 1.46379 6.91486 0.936153 6.58942 0.610713Z"/></svg>';

  // 현행 카드의 tb-store-mark 와 동일한 스토어 아이콘
  const STORE_ICON =
    '<svg class="ts-store-icon" width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round" aria-hidden="true"><path d="M2.2 5.6V11.8h9.6V5.6"/><path d="M1.2 5.6 2.6 2.2h8.8l1.4 3.4Z"/><path d="M5.5 11.8V8.6h3v3.2"/></svg>';

  function cardHtml(p) {
    const size = (u) => `${u}${u.includes('?') ? '&' : '?'}width=400`;
    const img = p.image ? size(p.image) : '';
    // 현행(PC): 두 번째 이미지가 있으면 호버 시 즉시 교체 (CSS 크로스페이드,
    // opacity 트랜지션 없음 — Boost settings.css 실측과 동일)
    const img2 =
      p.images && p.images.length > 1 && p.images[1] !== p.image
        ? size(p.images[1])
        : '';
    const instore =
      p.product_type === 'PRE-OWNED' &&
      p.seller_id != null &&
      INSTORE_SELLERS.has(String(p.seller_id));
    return `
<li class="ts-card" data-product-id="${p.shopify_product_id}">
  <a class="ts-card-link" href="${p.url}">
    <div class="ts-card-media${img2 ? ' ts-card-media--swap' : ''}">
      ${img ? `<img src="${img}" alt="" loading="lazy">` : '<div class="ts-card-noimg"><span>No Image</span></div>'}
      ${img2 ? `<img class="ts-card-img2" src="${img2}" alt="" loading="lazy">` : ''}
    </div>
    <div class="ts-card-body">
      <p class="ts-card-brand">${esc(p.brand)}</p>
      <p class="ts-card-title ts-card-title--grid">${esc(gridTitle(p))}</p>
      <p class="ts-card-title ts-card-title--list">${esc(listTitle(p))}</p>
      <p class="ts-card-meta">${esc(p.product_type)}${instore ? ` ${STORE_ICON}` : ''}</p>
      <p class="ts-card-price">
        ${p.discount_rate > 0 ? `<span class="ts-discount">${p.discount_rate}%</span>` : ''}
        ${money(p.price.usd)}
      </p>
    </div>
  </a>
  <div class="ts-card-actions">
    <a class="ts-btn ts-btn--primary" href="${p.url}">Select options</a>
    <a class="ts-btn ts-btn--secondary" href="${p.url}">Quick view</a>
  </div>
</li>`;
  }

  function esc(s) {
    return String(s ?? '').replace(
      /[&<>"']/g,
      (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
    );
  }

  // ── 렌더: 필터 (사이드바 + 드로어 공용) ─────────────────

  // 카테고리 트리(2026-09-21 확정): 1차 = Boost 어드민 설정 스냅샷
  // (계층·순서·표시명, 태그 기반 — 현행과 100% 동일), 과도기(API 가
  // 컬렉션 핸들을 내려줄 때) = 헤더 메뉴 파싱, 최후 = 태그 자체 트리.
  const CAT_SNAPSHOT = (() => {
    try {
      return JSON.parse(
        document.querySelector('[data-ts-cat-snapshot]')?.textContent || '[]',
      );
    } catch {
      return [];
    }
  })();
  const SNAP_NAMES = new Map(); // tag → 표시명 (요약 등)
  (function collectSnap(ns) {
    for (const n of ns || []) {
      SNAP_NAMES.set(n.t, n.d);
      collectSnap(n.c);
    }
  })(CAT_SNAPSHOT);

  /** 스냅샷 트리 + 태그 패싯 카운트 조인.
      숨김 규칙(현행 실측): 루트는 카운트 0 이면 숨김(카테고리 카운트는 자기
      선택 포함이라, 선택 시 다른 루트가 0 이 되어 사라진다). 하위는 카운트와
      무관하게 설정 트리 그대로 전부 표시 (현행 PC: 카운트도 루트에만). */
  function snapshotTree(values) {
    const counts = new Map(values.map((v) => [v.key, v]));
    const build = (ns, depth) =>
      (ns || [])
        .map((n) => {
          const hit = counts.get(n.t);
          const node = {
            key: n.t,
            label: n.d,
            doc_count: hit?.doc_count ?? 0,
            selected: hit?.selected ?? false,
            children: build(n.c, depth + 1),
          };
          if (depth === 0 && node.doc_count === 0 && !node.selected) return null;
          return node;
        })
        .filter(Boolean);
    return build(CAT_SNAPSHOT, 0);
  }

  const CAT_TOPS = (root.dataset.categoryTops || 'women,men,kids')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  let CAT_MENU = []; // [{h,t,c:[…]}]
  const CAT_TITLES = new Map(); // handle → 메뉴 표기 (요약·라벨 폴백)

  const handleOf = (a) =>
    (a.getAttribute('href') || '')
      .split('/collections/')[1]
      ?.split(/[?#/]/)[0] || null;

  /** 메뉴 전체 대문자 표기(WOMEN, BAGS)는 소문자로 — CSS capitalize 가 Bags 로 */
  const normalizeMenuLabel = (t) => (t === t.toUpperCase() ? t.toLowerCase() : t);

  function parseMenuNode(a, depth) {
    const h = handleOf(a);
    if (!h) return null;
    const node = { h, t: normalizeMenuLabel(a.textContent.trim()), c: [] };
    if (depth < 3) {
      const ul = a.parentElement?.querySelector(':scope > ul');
      if (ul) {
        for (const li of ul.children) {
          const ca = li.querySelector?.(':scope > a[href*="/collections/"]');
          if (!ca) continue;
          const child = parseMenuNode(ca, depth + 1);
          // 부모와 같은 컬렉션("All" 항목)은 건너뛴다
          if (child && child.h !== h) node.c.push(child);
        }
      }
    }
    return node;
  }

  /** 메가메뉴는 앱이 비동기로 그린다 — 있을 때만 파싱, 성공 여부 반환 */
  function ensureCatMenu() {
    if (CAT_MENU.length > 0) return true;
    const header = document.querySelector('header') || document;
    const anchors = [...header.querySelectorAll('a[href*="/collections/"]')];
    const tops = [];
    for (const h of CAT_TOPS) {
      const a = anchors.find((x) => handleOf(x) === h);
      if (!a) continue;
      const node = parseMenuNode(a, 0);
      if (node) tops.push(node);
    }
    if (tops.length === 0) return false;
    CAT_MENU = tops;
    (function collect(ns) {
      for (const n of ns) {
        CAT_TITLES.set(n.h, n.t);
        collect(n.c);
      }
    })(tops);
    return true;
  }

  let menuRetries = 0;
  function scheduleMenuRetry() {
    if (menuRetries >= 12) return; // ~6초까지 대기 후 포기
    menuRetries++;
    setTimeout(() => {
      if (ensureCatMenu()) {
        if (state.lastOptions) renderFacets(state.lastOptions);
      } else {
        scheduleMenuRetry();
      }
    }, 500);
  }

  /** 메뉴 트리 + 패싯 카운트 조인. 건수 0 이고 선택도 아니고 자식도 없는 노드는 숨김 */
  function menuTree(values) {
    const counts = new Map(values.map((v) => [v.key, v]));
    const build = (nodes) =>
      (nodes || [])
        .map((n) => {
          const hit = counts.get(n.h);
          const children = build(n.c);
          // 표시명: 컬렉션 제목이 전부 대문자면 운영 의도 표기(KIDS)로 그대로,
          // 아니면 메뉴 표기 (제목은 "women's tote & …" 처럼 길어 현행과 멀다)
          const apiLabel =
            hit && hit.label !== hit.key ? hit.label : null;
          const node = {
            key: n.h,
            label:
              apiLabel && apiLabel === apiLabel.toUpperCase()
                ? apiLabel
                : n.t || apiLabel,
            doc_count: hit?.doc_count ?? 0,
            selected: hit?.selected ?? false,
            children,
          };
          return node.doc_count > 0 || node.selected || children.length > 0
            ? node
            : null;
        })
        .filter(Boolean);
    return build(CAT_MENU);
  }

  function categoryTree(values) {
    // 태그 응답(현행 파리티 경로): 스냅샷 트리 — 없으면 태그 자체 트리
    if (values.some((v) => /^(women|men|golf|kids|life|unisex)_/i.test(v.key))) {
      return CAT_SNAPSHOT.length > 0 ? snapshotTree(values) : tagTree(values);
    }
    // 과도기(컬렉션 핸들 응답): 헤더 메뉴 파싱 트리
    if (ensureCatMenu()) return menuTree(values);
    scheduleMenuRetry(); // 메가메뉴가 아직 안 그려짐 — 그려지면 재렌더
    return [];
  }

  /** (폴백) 평면 태그(women, women_bags, …)를 트리로 */
  function tagTree(values) {
    const nodes = new Map();
    for (const v of values) {
      if (!CATEGORY_TAG.test(v.key)) continue;
      nodes.set(v.key, { ...v, children: [] });
    }
    const roots = [];
    for (const node of nodes.values()) {
      // 중간 계층 태그가 집계에 없을 수 있다 — 존재하는 가장 가까운 조상에 붙인다
      let parent = null;
      let key = node.key;
      for (let cut = key.lastIndexOf('_'); cut > 0; cut = key.lastIndexOf('_')) {
        key = key.slice(0, cut);
        parent = nodes.get(key);
        if (parent) break;
      }
      if (parent) parent.children.push(node);
      else roots.push(node);
    }
    for (const node of nodes.values()) {
      node.children.sort((a, b) => b.doc_count - a.doc_count);
    }
    return roots.sort((a, b) => b.doc_count - a.doc_count);
  }

  function catLabel(key) {
    const last = key.split('_').pop();
    return last.toUpperCase() === 'KIDS' ? 'KIDS' : last;
  }

  function categoryNodeHtml(node, depth) {
    const hasChildren = node.children.length > 0;
    const open = state.openCats.has(node.key) || node.selected;
    return `
<li class="ts-cat-node" style="--depth:${depth}">
  <div class="ts-cat-row${node.selected ? ' is-selected' : ''}">
    <button type="button" class="ts-cat-name" data-ts-cat-select data-key="${esc(node.key)}">${esc(node.label ?? catLabel(node.key))}</button>
    ${depth === 0 ? `<span class="ts-facet-count">(${node.doc_count})</span>` : ''}
    ${hasChildren ? `<button type="button" class="ts-cat-toggle${open ? ' is-open' : ''}" data-ts-cat-toggle data-key="${esc(node.key)}" aria-label="Expand">${CAT_ARROW_SVG}</button>` : ''}
  </div>
  ${
    hasChildren && open
      ? `<ul class="ts-cat-children">${node.children.map((c) => categoryNodeHtml(c, depth + 1)).join('')}</ul>`
      : ''
  }
</li>`;
  }

  function checkboxRows(opt, values) {
    return values
      .map((v) => {
        const paramKey =
          opt.filterOptionId === 'pf_p_price' ? v.key.replace('-', ':') : v.key;
        return `
<li>
  <label class="ts-facet-value${v.selected ? ' is-selected' : ''}">
    <input type="checkbox" ${v.selected ? 'checked' : ''}
      data-ts-facet-value data-param="${opt.filterOptionId}" data-key="${esc(paramKey)}">
    <span class="ts-facet-label">${esc(v.label || v.key)}</span>
    <span class="ts-facet-count">(${v.doc_count})</span>
  </label>
</li>`;
      })
      .join('');
  }

  /** 기존 UI 재현: 실범위(74~6514식) 자동 채움 + 듀얼 슬라이더 + 눈금 5개 */
  function priceGroupHtml(range) {
    const bound = range || { min: 0, max: PRICE_SLIDER_MAX }; // 구 API 폴백
    const raw = currentParams().get('pf_p_price') || ':';
    const [selMin, selMax] = raw.split(':');
    const lo = selMin !== '' ? Number(selMin) : bound.min;
    const hi = selMax !== '' ? Number(selMax) : bound.max;
    const tick = (i) => Math.round(bound.min + ((bound.max - bound.min) * i) / 4);
    const pct = (v) =>
      ((v - bound.min) / Math.max(1, bound.max - bound.min)) * 100;
    return `
<div class="ts-price" data-bound-min="${bound.min}" data-bound-max="${bound.max}">
  <div class="ts-price-inputs">
    <label>$ <input type="number" inputmode="numeric" value="${lo}" data-ts-price-min></label>
    <span class="ts-price-dash">-</span>
    <label>$ <input type="number" inputmode="numeric" value="${hi}" data-ts-price-max></label>
  </div>
  <div class="ts-price-slider">
    <div class="ts-price-track"></div>
    <div class="ts-price-connect" data-ts-price-connect style="left:${pct(lo)}%;width:${Math.max(0, pct(hi) - pct(lo))}%"></div>
    <input type="range" min="${bound.min}" max="${bound.max}" value="${lo}" data-ts-price-lo aria-label="Min price">
    <input type="range" min="${bound.min}" max="${bound.max}" value="${hi}" data-ts-price-hi aria-label="Max price">
  </div>
  <div class="ts-price-ticks">
    <span>${tick(0)}</span><span>${tick(1)}</span><span>${tick(2)}</span><span>${tick(3)}</span><span>${tick(4)}</span>
  </div>
</div>`;
  }

  /** 드래그 중 실시간 반영: 숫자 입력·연결바(teal)를 range 값에 맞춘다 */
  function syncPriceVisual(scope) {
    const boundMin = Number(scope.dataset.boundMin);
    const boundMax = Number(scope.dataset.boundMax);
    const lo = Number(scope.querySelector('[data-ts-price-lo]').value);
    const hi = Number(scope.querySelector('[data-ts-price-hi]').value);
    const pct = (v) =>
      ((v - boundMin) / Math.max(1, boundMax - boundMin)) * 100;
    const connect = scope.querySelector('[data-ts-price-connect]');
    const a = Math.min(lo, hi);
    const b = Math.max(lo, hi);
    connect.style.left = `${pct(a)}%`;
    connect.style.width = `${pct(b) - pct(a)}%`;
  }

  /** 현행 그룹 순서로 정렬 (미지의 그룹은 뒤로) */
  function sortedOptions(options) {
    const orderOf = (o) => {
      const i = GROUP_ORDER.indexOf(o.filterOptionId);
      return i < 0 ? GROUP_ORDER.length : i;
    };
    return [...options].sort((a, b) => orderOf(a) - orderOf(b));
  }

  /** 그룹 하나의 본문 html — 사이드바(details)와 드로어 드릴다운이 공유 */
  function buildGroupBody(opt) {
    if (opt.filterOptionId === 'pf_c_category') {
      const roots = categoryTree(opt.values);
      if (roots.length === 0) return '';
      return `<ul class="ts-cat-tree">${roots.map((r) => categoryNodeHtml(r, 0)).join('')}</ul>`;
    }
    if (opt.filterOptionId === 'pf_p_price') {
      // 현행: 가격 범위가 한 점이면(결과 1건 등 min==max) 그룹 자체를 숨긴다
      // — 사이드바·드로어 공통. 단 가격 필터가 걸려 있으면 해제할 수 있게 유지.
      const priceSelected = currentParams().has('pf_p_price');
      if (!priceSelected && opt.range && opt.range.max <= opt.range.min) {
        return '';
      }
      return priceGroupHtml(opt.range);
    }
    let values = opt.values.filter((v) => v.doc_count > 0 || v.selected);
    // 현행 순서: Product Type 은 NEW → PRE-OWNED (건수순 아님)
    if (opt.filterOptionId === 'pf_pt_product_type') {
      values = [...values].sort((a, b) =>
        (a.label || a.key).localeCompare(b.label || b.key),
      );
    }
    let search = '';
    if (opt.filterOptionId === 'pf_v_brand') {
      search = `<input type="text" class="ts-brand-search" placeholder="Search Options" value="${esc(state.brandFilter)}" data-ts-brand-search>`;
      if (state.brandFilter) {
        const f = state.brandFilter.toLowerCase();
        values = values.filter((v) =>
          (v.label || v.key).toLowerCase().includes(f),
        );
      }
    }
    if (values.length === 0 && !search) return '';
    return `${search}<ul class="ts-facet-values">${checkboxRows(opt, values)}</ul>`;
  }

  /** PC 사이드바 상단 "Refine By" — 현행 클론: 선택 필터 목록 + 개별 ×·Clear All.
      카테고리는 현행처럼 2줄 — "Category: 루트(원시 표기)" + 하위 선택 시
      "Collection Tag: 태그(원시)". Category × = 전체 해제, Collection Tag × =
      루트만 남김 (Boost 의 컬렉션+태그 이중 파라미터와 동일한 거동). */
  const REFINE_X_SVG =
    '<svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M2 2l12 12M14 2L2 14"/></svg>';
  function refineByHtml(options) {
    const items = [];
    for (const opt of sortedOptions(options)) {
      if (opt.filterOptionId === 'pf_p_price') {
        const picked = selectedSummary(opt);
        if (picked) items.push({ id: opt.filterOptionId, key: '', label: opt.label, value: picked });
        continue;
      }
      for (const v of opt.values.filter((x) => x.selected)) {
        let value = v.label || v.key;
        if (opt.filterOptionId === 'pf_c_category') {
          const root = v.key.split('_')[0];
          items.push({ id: opt.filterOptionId, key: v.key, label: opt.label, value: root });
          if (root !== v.key) {
            items.push({ id: opt.filterOptionId, key: v.key, label: 'Collection Tag', value: v.key, toRoot: root });
          }
          continue;
        }
        if (opt.filterOptionId === 'pf_v_brand') {
          value = value.toUpperCase(); // 현행: 원 벤더 표기(대문자)
        }
        items.push({ id: opt.filterOptionId, key: v.key, label: opt.label, value });
      }
    }
    if (items.length === 0) return '';
    return `
<div class="ts-refineby">
  <div class="ts-refineby-title">
    <span class="ts-refineby-heading">Refine By</span>
    <button type="button" class="ts-refineby-clear" data-ts-clear-side>Clear All</button>
  </div>
  <ul class="ts-refineby-list">${items
    .map(
      (i) =>
        `<li class="ts-refineby-item"><span class="ts-refineby-text"><span class="ts-refineby-option">${esc(i.label)}:&nbsp;</span><span class="ts-refineby-value">${esc(i.value)}</span></span><button type="button" class="ts-refineby-x" data-ts-unfilter data-facet="${esc(i.id)}" data-key="${esc(i.key)}"${i.toRoot ? ` data-to-root="${esc(i.toRoot)}"` : ''} aria-label="Remove ${esc(i.value)}">${REFINE_X_SVG}</button></li>`,
    )
    .join('')}</ul>
</div>`;
  }

  function facetsHtml(options) {
    return (
      refineByHtml(options) +
      sortedOptions(options)
        .map((opt) => {
          const body = buildGroupBody(opt);
          if (!body) return '';
          // 현행: 그룹에 선택이 있으면 헤딩 우측에 CLEAR (그 그룹만 해제)
          const hasSel = selectedSummary(opt) !== '';
          return `
<details class="ts-facet-group" open>
  <summary class="ts-facet-heading"><span class="ts-facet-heading-text">${esc(opt.label)}</span>${hasSel ? `<button type="button" class="ts-group-clear" data-ts-group-clear data-facet="${esc(opt.filterOptionId)}">Clear</button>` : ''}</summary>
  ${body}
</details>`;
        })
        .join('')
    );
  }

  /** 그룹의 선택값 요약 — 현행: 루트 행 제목 아래에 표시 ("Women", "PRE-OWNED, NEW" …) */
  function selectedSummary(opt) {
    if (opt.filterOptionId === 'pf_p_price') {
      const raw = currentParams().get('pf_p_price');
      if (!raw) return '';
      const [lo, hi] = raw.split(':');
      return [lo && `$${lo}`, hi && `$${hi}`].filter(Boolean).join(' - ');
    }
    const picked = opt.values.filter((v) => v.selected);
    if (opt.filterOptionId === 'pf_c_category') {
      // 현행: 하위를 골라도 요약에는 최상위(Women/Men/KIDS)만 표시
      return picked
        .map((v) => {
          const root = v.key.split('_')[0];
          return (
            SNAP_NAMES.get(root) ??
            CAT_TITLES.get(root) ??
            SNAP_NAMES.get(v.key) ??
            CAT_TITLES.get(v.key) ??
            catLabel(v.key)
          );
        })
        .join(', ');
    }
    if (opt.filterOptionId === 'pf_v_brand') {
      // 현행: 원 벤더 표기(대문자) — 우리 키는 normalized 소문자라 복원
      return picked.map((v) => (v.label || v.key).toUpperCase()).join(', ');
    }
    return picked.map((v) => v.label || v.key).join(', ');
  }

  /** 모바일 드로어 — 현행 클론: 루트=그룹 행 목록(카운트 없음), 행 탭=드릴다운 */
  function drawerHtml(options) {
    if (state.drawerGroup) {
      const opt = options.find((o) => o.filterOptionId === state.drawerGroup);
      const body = opt ? buildGroupBody(opt) : '';
      if (body) return `<div class="ts-dlevel">${body}</div>`;
      state.drawerGroup = null; // 그룹이 사라졌으면 루트로
    }
    return sortedOptions(options)
      .map((opt) => {
        if (!buildGroupBody(opt)) return '';
        const picked = selectedSummary(opt);
        return `<button type="button" class="ts-dgroup" data-ts-dgroup="${esc(opt.filterOptionId)}"><span class="ts-dgroup-text"><span class="ts-dgroup-title">${esc(opt.label)}</span>${picked ? `<span class="ts-dgroup-value">${esc(picked)}</span>` : ''}</span>${CAT_ARROW_SVG}</button>`;
      })
      .join('');
  }

  function renderDrawer() {
    if (!state.lastOptions) return;
    els.drawerFacets.innerHTML = drawerHtml(state.lastOptions);
    const open =
      state.drawerGroup &&
      state.lastOptions.find((o) => o.filterOptionId === state.drawerGroup);
    els.drawerTitle.textContent = open ? open.label : 'Refine By';
    els.drawerBack.hidden = !open;
    // 현행: 선택된 필터가 있을 때만 CLEAR ALL 노출
    const hasSelection = state.lastOptions.some(
      (o) => selectedSummary(o) !== '',
    );
    els.clearAll.hidden = !hasSelection;
  }

  function renderFacets(options) {
    state.lastOptions = options; // 로컬 재렌더(카테고리 펼침·브랜드 검색)용 캐시
    els.sidebar.innerHTML = facetsHtml(options);
    // 결과 0건(빈 컬렉션·검색 0건)이면 쓸 수 있는 필터가 없는데 사이드바 240px 자리(빈 Brand 제목·가격
    // 슬라이더)가 남아 본문이 오른쪽으로 밀린다 → 숨김. 단 사용자가 고른 필터가 있으면 유지 — 0건이 된
    // 조합을 사이드바에서 풀 수 있어야 한다
    const hasFacetParam = [...currentParams().keys()].some((k) =>
      FACET_PARAMS.includes(k),
    );
    const nothing = options.length === 0 || state.lastTotal === 0;
    els.sidebar.hidden = nothing && !hasFacetParam;
    renderDrawer();
  }

  // ── 렌더: 헤더·탭·툴바 ─────────────────────────────────

  // ── 정렬 드롭다운 (현행 클론: 우측 정렬 250px 패널, 활성=밑줄) ──────
  function selectedSortText() {
    return els.sort.options[els.sort.selectedIndex]?.text || 'Top Rated';
  }

  function buildSortList() {
    els.sortList.innerHTML = [...els.sort.options]
      .map(
        (o) =>
          `<li class="ts-sort-option${o.value === els.sort.value ? ' is-active' : ''}" role="option" aria-selected="${o.value === els.sort.value}" data-ts-sort-option data-value="${esc(o.value)}">${esc(o.text)}</li>`,
      )
      .join('');
  }

  function openSortList(open) {
    if (open) buildSortList();
    els.sortList.hidden = !open;
    els.sortBtn.setAttribute('aria-expanded', String(open));
  }

  function renderChrome(res) {
    const q = currentParams().get('q') || '';
    els.title.textContent = COLLECTION_HANDLE
      ? res.collection?.title || COLLECTION_TITLE
      : q
        ? `Search results for "${q}"`
        : 'All products';
    els.input.value = q;
    const n = String(res.total_product); // 현행은 콤마 없는 정수 표기
    els.count.textContent = `${n} products`;
    els.showing.textContent = `Showing ${n} results`;
    els.showResult.textContent = `Show result (${n})`; // 현행 드로어 하단 버튼
    document.querySelector('[data-ts-tab="products"]').textContent =
      `Products (${n})`;
    els.tabs.hidden = false;
    els.toolbar.hidden = false;
    els.sort.value =
      currentParams().get('sort') || 'number-extra-sort1-descending';
    els.sortValue.textContent = selectedSortText();
  }

  async function renderResourceTabs(q) {
    const { collections, pages, counts } = await fetchResources(q);
    const tabC = document.querySelector('[data-ts-tab="collections"]');
    const tabP = document.querySelector('[data-ts-tab="pages"]');
    tabC.hidden = counts.c === 0;
    tabC.textContent = `Collections (${counts.c})`;
    tabP.hidden = counts.p === 0;
    tabP.textContent = `Blogs & Pages (${counts.p})`;
    state.resources = { collections, pages };
  }

  /** 현행 클론: 항목 = 제목(+설명 2줄) 세로 리스트, 패딩 15px 0.
   *  설명은 컬렉션 탭만 — 현행 실측상 페이지/블로그 항목은 제목만 표시한다. */
  function resourceListHtml(items, { withDesc } = { withDesc: false }) {
    if (!items.length) return '<p class="ts-status">No results.</p>';
    return `<div class="ts-resource-list">${items
      .map(
        (r) => `
<a class="ts-resource-item" href="${esc(r.url)}">
  <p class="ts-resource-title">${esc(r.title)}</p>
  ${withDesc && r.description ? `<p class="ts-resource-desc">${esc(r.description)}</p>` : ''}
</a>`,
      )
      .join('')}</div>`;
  }

  // ── 로드 사이클 ────────────────────────────────────────

  /** 로딩 자리표시 — 현행(Boost)도 'Loading…' 텍스트가 아니라 placeholder
   *  카드(boost-sd__product-list-placeholder-item)를 쓴다. 결과 그리드와
   *  같은 모양의 쉬머 카드라 레이아웃 점프(CLS)가 없다. */
  function skeletonHtml(n) {
    return Array.from(
      { length: n },
      () => `
<li class="ts-card ts-card--skel" data-skel aria-hidden="true">
  <div class="ts-card-media"></div>
  <div class="ts-card-body">
    <p class="ts-skel-line" style="width:40%"></p>
    <p class="ts-skel-line" style="width:90%"></p>
    <p class="ts-skel-line" style="width:65%"></p>
    <p class="ts-skel-line" style="width:30%"></p>
  </div>
</li>`,
    ).join('');
  }
  const clearSkeletons = () =>
    els.grid.querySelectorAll('[data-skel]').forEach((el) => el.remove());

  async function load(page, { append, prepend } = {}) {
    if (state.loading) return;
    state.loading = true;
    els.moreBtn && (els.moreBtn.disabled = true);
    if (!append && !prepend) {
      els.status.hidden = true;
      if (!els.grid.childElementCount || els.grid.querySelector('[data-skel]')) {
        // 첫 로드: 빈 화면 + 'Loading…' 대신 스켈레톤 그리드
        els.grid.innerHTML = skeletonHtml(12);
      } else {
        // 필터·정렬 재조회: 기존 결과를 비우지 않고 디밍한 채 유지
        els.grid.classList.add('ts-grid--refetch');
      }
    } else if (append) {
      // 무한 스크롤: 하단에 다음 페이지 자리표시
      els.grid.insertAdjacentHTML('beforeend', skeletonHtml(4));
    }
    try {
      const res = await fetchPage(page);
      if (prepend) {
        // 이전 페이지를 위에 붙이고, 보고 있던 카드가 밀리지 않게 스크롤 보정
        state.minPage = res.page;
        const anchor = els.grid.firstElementChild;
        const topBefore = anchor ? anchor.getBoundingClientRect().top : 0;
        els.grid.insertAdjacentHTML(
          'afterbegin',
          res.products.map(cardHtml).join(''),
        );
        if (anchor) {
          window.scrollBy(0, anchor.getBoundingClientRect().top - topBefore);
        }
        els.prev.hidden = state.minPage <= 1;
        window.bucksCC?.reConvert?.();
        return;
      }
      state.page = res.page;
      state.totalPage = res.total_page;
      if (!append) state.minPage = res.page;
      renderChrome(res);
      state.lastTotal = res.total_product; // renderFacets 의 0건 사이드바 숨김 판단용
      renderFacets(res.filter.options);
      const html = res.products.map(cardHtml).join('');
      if (append) {
        clearSkeletons();
        els.grid.insertAdjacentHTML('beforeend', html);
      } else {
        els.grid.innerHTML = html;
      }
      els.status.hidden = res.total_product > 0;
      if (res.total_product === 0) {
        els.status.textContent = COLLECTION_HANDLE
          ? 'No products found.'
          : 'No results found. Try a different search.';
      }
      els.more.hidden = state.page >= state.totalPage;
      els.prev.hidden = state.minPage <= 1; // 현행: page>1 진입 시 위쪽에 노출
      syncPageParam(state.page);
      root.dataset.rid = res.meta?.rid || ''; // 이벤트 수집(후속) 조인 키
      window.bucksCC?.reConvert?.(); // 통화앱 KRW 변환 트리거
    } catch (e) {
      clearSkeletons(); // 첫 로드 실패 시 스켈레톤 잔류 방지
      els.status.hidden = false;
      els.status.textContent =
        "We couldn't load the results. Please try again in a moment.";
      console.error('[trenbe-search]', e);
    } finally {
      els.grid.classList.remove('ts-grid--refetch');
      state.loading = false;
      els.moreBtn && (els.moreBtn.disabled = false);
    }
  }

  // ── 이벤트 배선 ────────────────────────────────────────

  els.clearQ.addEventListener('click', () => {
    els.input.value = '';
    els.input.focus();
  });

  els.moreBtn.addEventListener('click', () =>
    load(state.page + 1, { append: true }),
  );

  // 무한 스크롤 (현행과 동일) — 센티널이 보이면 다음 페이지 자동 로드
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        if (
          entries.some((en) => en.isIntersecting) &&
          !state.loading &&
          state.page < state.totalPage
        ) {
          load(state.page + 1, { append: true });
        }
      },
      { rootMargin: '600px 0px' },
    ).observe(els.more);
  } else {
    els.moreBtn.style.display = 'inline-block'; // 구형 브라우저 폴백
  }

  function applySort(value) {
    els.sort.value = value;
    els.sortValue.textContent = selectedSortText();
    const params = currentParams();
    params.set('sort', value);
    writeUrl(params);
    load(1);
  }

  els.sortBtn.addEventListener('click', () =>
    openSortList(els.sortList.hidden),
  );
  els.sortList.addEventListener('click', (e) => {
    const opt = e.target.closest('[data-ts-sort-option]');
    if (!opt) return;
    openSortList(false);
    applySort(opt.dataset.value);
  });
  document.addEventListener('click', (e) => {
    if (!els.sortList.hidden && !els.sortRoot.contains(e.target)) {
      openSortList(false);
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') openSortList(false);
  });

  els.prevBtn.addEventListener('click', () =>
    load(state.minPage - 1, { prepend: true }),
  );

  // View as (grid / list)
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ts-view]');
    if (!btn) return;
    state.view = btn.dataset.tsView || btn.getAttribute('data-ts-view');
    root
      .querySelectorAll('[data-ts-view]')
      .forEach((b) => b.classList.toggle('is-active', b === btn));
    els.grid.classList.toggle('is-list', state.view === 'list');
  });

  // 탭 전환
  els.tabs.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-ts-tab]');
    if (!tab) return;
    els.tabs
      .querySelectorAll('[data-ts-tab]')
      .forEach((t) => t.classList.toggle('is-active', t === tab));
    const kind = tab.dataset.tsTab;
    els.panelProducts.hidden = kind !== 'products';
    els.panelResources.hidden = kind === 'products';
    if (kind !== 'products') {
      els.panelResources.innerHTML =
        kind === 'collections'
          ? resourceListHtml(state.resources?.collections || [], {
              withDesc: true,
            })
          : resourceListHtml(state.resources?.pages || []);
    }
  });

  // 드로어 (모바일) — 열 때는 항상 루트 목록부터 (현행 동일)
  const setDrawer = (open) => {
    if (open) {
      state.drawerGroup = null;
      renderDrawer();
    }
    els.drawer.hidden = !open;
    els.overlay.hidden = !open;
    document.documentElement.classList.toggle('ts-drawer-open', open);
  };
  els.refine.addEventListener('click', () => setDrawer(true));
  els.close.addEventListener('click', () => setDrawer(false));
  els.overlay.addEventListener('click', () => setDrawer(false));
  els.showResult.addEventListener('click', () => setDrawer(false));
  els.drawerBack.addEventListener('click', () => {
    state.drawerGroup = null;
    renderDrawer();
  });
  // CLEAR ALL — 전체 필터 해제 (드로어 상단 + PC Refine By 공용)
  function clearAllFacets() {
    const params = currentParams();
    for (const k of FACET_PARAMS) params.delete(k);
    writeUrl(params);
    load(1);
  }
  els.clearAll.addEventListener('click', clearAllFacets);

  // 필터 조작 — 사이드바·드로어 공용 (위임)
  root.addEventListener('change', (e) => {
    const input = e.target.closest('[data-ts-facet-value]');
    if (input) {
      toggleFacetValue(input.dataset.param, input.dataset.key);
      return;
    }
    if (
      e.target.matches(
        '[data-ts-price-min], [data-ts-price-max], [data-ts-price-lo], [data-ts-price-hi]',
      )
    ) {
      const scope = e.target.closest('.ts-price');
      if (e.target.matches('[data-ts-price-lo]')) {
        scope.querySelector('[data-ts-price-min]').value = e.target.value;
      }
      if (e.target.matches('[data-ts-price-hi]')) {
        scope.querySelector('[data-ts-price-max]').value = e.target.value;
      }
      const boundMin = Number(scope.dataset.boundMin);
      const boundMax = Number(scope.dataset.boundMax);
      let lo = Number(scope.querySelector('[data-ts-price-min]').value);
      let hi = Number(scope.querySelector('[data-ts-price-max]').value);
      if (Number.isNaN(lo)) lo = boundMin;
      if (Number.isNaN(hi)) hi = boundMax;
      if (lo > hi) [lo, hi] = [hi, lo];
      // 경계 그대로면 필터 없음 (기존 UI 와 동일 의미)
      if (lo <= boundMin && hi >= boundMax) setPriceRange('', '');
      else setPriceRange(String(lo), String(hi));
    }
  });

  root.addEventListener('input', (e) => {
    // 슬라이더 드래그 중 숫자 입력·연결바 실시간 동기화 (요청은 change 에서)
    if (e.target.matches('[data-ts-price-lo], [data-ts-price-hi]')) {
      const scope = e.target.closest('.ts-price');
      const isLo = e.target.matches('[data-ts-price-lo]');
      scope.querySelector(isLo ? '[data-ts-price-min]' : '[data-ts-price-max]').value =
        e.target.value;
      syncPriceVisual(scope);
      return;
    }
    if (e.target.matches('[data-ts-brand-search]')) {
      state.brandFilter = e.target.value;
      // 재렌더 시 포커스가 날아가므로 커서를 복원한다
      if (state.lastOptions) {
        const active = document.activeElement === e.target;
        renderFacets(state.lastOptions);
        if (active) {
          const again = (
            e.target.closest('[data-ts-drawer-facets]')
              ? els.drawerFacets
              : els.sidebar
          ).querySelector('[data-ts-brand-search]');
          again.focus();
          again.setSelectionRange(again.value.length, again.value.length);
        }
      }
    }
  });

  root.addEventListener('click', (e) => {
    // PC Refine By — 필 개별 해제 / Clear All (현행 동일)
    const unf = e.target.closest('[data-ts-unfilter]');
    if (unf) {
      if (unf.dataset.facet === 'pf_p_price') {
        setPriceRange('', '');
      } else if (unf.dataset.toRoot) {
        // Collection Tag × — 현행처럼 태그만 벗기고 루트 컬렉션은 남긴다
        const params = currentParams();
        params.set('pf_c_category', unf.dataset.toRoot);
        writeUrl(params);
        load(1);
      } else {
        toggleFacetValue(unf.dataset.facet, unf.dataset.key); // 선택값 재토글 = 해제
      }
      return;
    }
    if (e.target.closest('[data-ts-clear-side]')) {
      clearAllFacets();
      return;
    }
    // PC 그룹 헤딩의 CLEAR — 그 그룹만 해제 (현행: 선택 있을 때만 노출)
    const gc = e.target.closest('[data-ts-group-clear]');
    if (gc) {
      e.preventDefault(); // details 접힘 방지
      const params = currentParams();
      params.delete(gc.dataset.facet);
      writeUrl(params);
      load(1);
      return;
    }
    const dg = e.target.closest('[data-ts-dgroup]');
    if (dg) {
      state.drawerGroup = dg.dataset.tsDgroup;
      renderDrawer();
      return;
    }
    const sel = e.target.closest('[data-ts-cat-select]');
    if (sel) {
      toggleFacetValue('pf_c_category', sel.dataset.key);
      return;
    }
    const tog = e.target.closest('[data-ts-cat-toggle]');
    if (tog) {
      const key = tog.dataset.key;
      state.openCats.has(key)
        ? state.openCats.delete(key)
        : state.openCats.add(key);
      if (state.lastOptions) renderFacets(state.lastOptions);
    }
  });

  // 컬렉션 모드: 검색 폼·탭 불필요, 정렬에 큐레이션 순서(Featured=manual) 추가
  if (COLLECTION_HANDLE) {
    root.classList.add('is-collection');
    const opt = document.createElement('option');
    opt.value = 'manual';
    opt.textContent = 'Featured';
    els.sort.prepend(opt);
  }

  // monthly-deals 할인율 탭(커스텀 Liquid 섹션)은 pushState 후 인공 popstate
  // 를 쏘는 방식으로 재필터를 요청한다 (Boost 시절부터의 계약). 위젯 자신은
  // replaceState 만 쓰므로 popstate 는 그 탭 전환과 그 사이의 브라우저
  // 뒤/앞으로 가기에서만 발생한다 — URL 을 진실로 삼아 1페이지부터 재조회.
  window.addEventListener('popstate', () => load(1));

  // 현행처럼 ?page=N 진입 시 그 페이지부터 시작 (위로는 Load Previous Page)
  const initPage = Math.max(
    1,
    Number(new URLSearchParams(location.search).get('page')) || 1,
  );
  load(initPage);
  if (!COLLECTION_HANDLE) {
    renderResourceTabs(currentParams().get('q') || '');
  }
})();
